import './resourceConfigurationHoc.css';

import { Col, Container, Row } from 'react-bootstrap';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { AwsAccountDropdown } from './AwsAccountDropdown';
import ConfirmationDialog from '../../../../global/confirmation-dialog/ConfirmationDialog';
import { DynamicTable } from '../../../../global/dynamic-table/DynamicTable';
import { EditableDropdownCell } from '../../../../global/dynamic-table/EditableDropdownCell';
import { EditableInputCell } from '../../../../global/dynamic-table/EditableInputCell';
import TableActionButtons from '../../../../global/table-action-buttons/TableActionButtons';
import { TenantIdContext } from '../../../../components/app/App';
import Text from '@amzn/meridian/text';
import { getActiveWorkflowsWithTenantResource } from '../../services/api-calls';
import { nanoid } from 'nanoid';
import { tenantAwsAccountsSubject$ } from '../../../../global/api-calls';

/*
Creates a page for tenant resources within a dynamic table.
tableHeaderDisplayOptions contains all the configurations required for the data columns.

example usage:

const foobar = resourceConfigurationHoc(
  'Foobar Page Name',
  {
    uuid: { // 'id' is reserved and cannot be used
      header: 'uuid column header',
      type: 'text',
      width: 500, // default 150
    },
    awsAccountId: {
      header: 'Aws Account Id',
      type: 'editableInput',
      validationHandler: async (input) => {
        return myValidation(); // failed validations should throw Error('errorMsg for user')
      },
    },
    engine: {
      header: 'Engine',
      type: 'editableSelect',
      selectOptions: ['optionA', 'optionB'],
      validationHandler: async (input) => {
        return myValidation();
      },
    },
    model: {
      header: 'Model',
      type: 'editableInput',
    },
  },
  addResourceCallback,
  removeResourceCallback,
  getResourcesCallback,
);


Note that validationHandler must return a promise.
*/
const resourceConfigurationHoc = (
  resourceName,
  tableHeaderDisplayOptions,
  addTenantWrappedResource,
  updateTenantWrappedResource,
  removeTenantWrappedResource,
  getTenantWrappedResources,
  showAwsAccountDropdown = true,
  resourceEditable = true,
  resourceRemovable = true
) => {
  return ({ setActivePath }) => {
    const tenantId = useContext(TenantIdContext);

    const [awsAccounts, setAwsAccounts] = useState([]);
    const [activeAwsAccount, setActiveAwsAccount] = useState('');
    const [tenantResourcesData, setTenantResourcesData] = useState([]);
    const [hasTenantResourcesDataChangedByUser, setHasTenantResourcesDataChangedByUser] = useState(false);

    const [editConfirmationText, setEditConfirmationText] = useState('');
    const [isEditConfirmationDialogOpen, setIsEditConfirmationDialogOpen] = useState(false);
    const [editConfirmationCallback, setEditConfirmationCallback] = useState(() => {});

    const [deleteConfirmationText, setDeleteConfirmationText] = useState(
      'Are you sure you would like to remove this entry?'
    );
    const [isDeleteConfirmationDisabled, setIsDeleteConfirmationDisabled] = useState(false);
    const [isDeleteConfirmationDialogOpen, setIsDeleteConfirmationDialogOpen] = useState(false);
    const [deleteConfirmationCallback, setDeleteConfirmationCallback] = useState(() => {});

    // override getRowId - https://react-table.tanstack.com/docs/api/useTable#table-options
    const getRowId = row => {
      return row.id;
    };

    const saveResource = useMemo(
      () => (rowIndex, savedResource, isNew) => {
        let saveTenantWrappedResource = updateTenantWrappedResource;
        if (isNew) {
          saveTenantWrappedResource = addTenantWrappedResource;
        }
        return saveTenantWrappedResource({ tenantId: tenantId, resource: savedResource }).then(response => {
          if (!response?.data) {
            return;
          }

          setTenantResourcesData(oldTenantResourcesData => {
            let isNewResourceAdded = false;

            const data = oldTenantResourcesData.map((resource, index) => {
              if (rowIndex === index) {
                const updatedResource = { ...resource };
                if (resource.isNew) {
                  isNewResourceAdded = true;
                  updatedResource['uuid'] = response.data.resourceSk;
                  updatedResource['isEdit'] = false;
                  updatedResource['isNew'] = false;
                  updatedResource['original'] = { ...updatedResource };
                } else {
                  updatedResource['isEdit'] = false;
                  updatedResource['original'] = { ...updatedResource };
                }
                return updatedResource;
              } else {
                return { ...resource };
              }
            });

            if (isNewResourceAdded) {
              const newResource = addRowForNewResource();
              data.push(newResource);
            }

            return data;
          });
        });
      },
      [tenantId]
    );

    const removeResource = useMemo(
      () => (rowIndex, removedResource) => {
        return removeTenantWrappedResource({ tenantId: tenantId, resourceId: removedResource.uuid }).then(response => {
          if (!response?.data) {
            return;
          }

          setTenantResourcesData(oldTenantResourcesData => {
            var data = [...oldTenantResourcesData]; // create copy since splice is in place
            data.splice(rowIndex, 1);
            return data;
          });
        });
      },
      [tenantId]
    );

    const editEntryConfirmation = useMemo(
      () => (rowIndex, rowItem) => {
        const entryToEdit = rowItem.values;
        if (!rowItem.original.isNew) {
          getActiveWorkflowsWithTenantResource({ tenantId: tenantId, resourceId: entryToEdit.uuid }).then(response => {
            if (Object.keys(response?.data).length > 0) {
              setEditConfirmationText(
                `Workflows [${Object.keys(response?.data).join(
                  ', '
                )}] that uses this resource will also update, are you sure?`
              );
              // for explanation on why this is a double lambda, see
              // https://medium.com/swlh/how-to-store-a-function-with-the-usestate-hook-in-react-8a88dd4eede1
              setEditConfirmationCallback(() => () => saveResource(rowIndex, entryToEdit, false));
              setIsEditConfirmationDialogOpen(true);
            } else {
              saveResource(rowIndex, entryToEdit, false);
            }
          });
        } else {
          saveResource(rowIndex, entryToEdit, true);
        }
      },
      [tenantId, saveResource]
    );

    const deleteEntryConfirmation = useMemo(
      () => (rowIndex, rowItem) => {
        const entryToRemove = rowItem.values;
        getActiveWorkflowsWithTenantResource({ tenantId: tenantId, resourceId: entryToRemove.uuid }).then(response => {
          if (Object.keys(response?.data).length > 0) {
            setDeleteConfirmationText(
              `Cannot delete resource that is in use by workflows [${Object.keys(response?.data).join(', ')}]`
            );
            setIsDeleteConfirmationDisabled(true);
          }

          // for explanation on why this is a double lambda, see
          // https://medium.com/swlh/how-to-store-a-function-with-the-usestate-hook-in-react-8a88dd4eede1
          setDeleteConfirmationCallback(() => () => removeResource(rowIndex, entryToRemove));
          setIsDeleteConfirmationDialogOpen(true);
        });
      },
      [tenantId, removeResource]
    );

    useEffect(() => {
      function isResourceDataValid(resourceData) {
        return Promise.all(
          Object.entries(tableHeaderDisplayOptions).map(async ([key, columnOptions]) => {
            const validator = columnOptions.validationHandler;
            if (validator) {
              return validator(resourceData[key])
                .then(res => {
                  return true;
                })
                .catch(error => {
                  return false;
                });
            } else {
              return true;
            }
          })
        ).then(columnValidations => columnValidations.every(v => v === true));
      }

      if (hasTenantResourcesDataChangedByUser) {
        Promise.all(
          tenantResourcesData.map((resourceData, index) => {
            return isResourceDataValid(resourceData).then(isValid =>
              Promise.resolve({ ...resourceData, isValid: isValid })
            );
          })
        ).then(data => {
          setHasTenantResourcesDataChangedByUser(false);
          setTenantResourcesData([...data]);
        });
      }
    }, [hasTenantResourcesDataChangedByUser, tenantResourcesData]);

    // set table headers
    const tableHeaders = useMemo(() => {
      const handleOnChangeCell = (rowIndex, columnId, newValue) => {
        setTenantResourcesData(oldTenantResourcesData => {
          setHasTenantResourcesDataChangedByUser(true);
          return oldTenantResourcesData.map((value, index) => {
            if (index === rowIndex) {
              const newResourceData = {
                ...oldTenantResourcesData[rowIndex],
                [columnId]: newValue,
              };
              return newResourceData;
            } else {
              return value;
            }
          });
        });
      };

      const handleClickEditRow = (rowIndex, isEdit) => {
        setTenantResourcesData(oldTenantResourcesData =>
          oldTenantResourcesData.map((value, index) => {
            if (rowIndex === index) {
              if (isEdit) {
                return { ...value, isEdit: true };
              } else {
                // reset row to original values from database, also sets `original` so it can rollback edits again
                return { ...value.original, original: { ...value.original } };
              }
            } else {
              return { ...value };
            }
          })
        );
      };

      let headers = [];
      for (const [key, columnOptions] of Object.entries(tableHeaderDisplayOptions)) {
        headers.push({
          Header: columnOptions.header,
          accessor: key,
          className: columnOptions.className,
          width: columnOptions.width ? columnOptions.width : 150, // width default is 150 - https://react-table.tanstack.com/docs/api/useTable#column-options
          Cell: ({ value, column, row }) => {
            if (columnOptions.type === 'editableSelect') {
              return (
                <EditableDropdownCell
                  row={row}
                  column={column}
                  value={value}
                  onChange={newValue => {
                    handleOnChangeCell(row.index, column.id, newValue);
                  }}
                  selectOptions={columnOptions.selectOptions}
                  validationHandler={columnOptions.validationHandler}
                />
              );
            } else if (columnOptions.type === 'editableInput') {
              return (
                <EditableInputCell
                  row={row}
                  column={column}
                  value={value}
                  onChange={newValue => {
                    handleOnChangeCell(row.index, column.id, newValue);
                  }}
                  validationHandler={columnOptions.validationHandler}
                />
              );
            } else {
              return <Text>{value}</Text>;
            }
          },
        });
      }

      // add column for record actions
      headers.push({
        Header: 'Actions',
        accessor: 'manageEntry',
        width: 175,
        disableResizing: true,
        disableSortBy: true,
        Cell: ({ column, row }) => {
          if (row.original.isNew === true) {
            return (
              <TableActionButtons
                editable={true}
                removable={resourceRemovable}
                row={row}
                onEdit={item => {
                  handleClickEditRow(item.index, true);
                }}
                onDiscardEdits={item => {
                  handleClickEditRow(item.index, false);
                }}
                onSave={item => {
                  editEntryConfirmation(item.index, item);
                }}
                onRemove={item => {
                  deleteEntryConfirmation(item.index, item);
                }}
              />
            );
          } else {
            return (
              <TableActionButtons
                editable={resourceEditable}
                removable={resourceRemovable}
                row={row}
                onEdit={item => {
                  handleClickEditRow(item.index, true);
                }}
                onDiscardEdits={item => {
                  handleClickEditRow(item.index, false);
                }}
                onSave={item => {
                  editEntryConfirmation(item.index, item);
                }}
                onRemove={item => {
                  deleteEntryConfirmation(item.index, item);
                }}
              />
            );
          }
        },
      });
      return {
        columns: headers,
      };
    }, [editEntryConfirmation, deleteEntryConfirmation]);

    const addRowForNewResource = () => {
      // add new blank row for add action
      const emptyResource = { id: nanoid(), isNew: true, isEdit: false };
      Object.entries(tableHeaderDisplayOptions).map(([key, value]) => (emptyResource[key] = ''));
      emptyResource['original'] = { ...emptyResource };
      return emptyResource;
    };

    useEffect(() => {
      const tenantAwsAccountsSubscription = tenantAwsAccountsSubject$.subscribe(value => {
        if (!value) {
          return;
        }
        setAwsAccounts(value);
      });

      return () => tenantAwsAccountsSubscription.unsubscribe();
    }, [tenantId]);

    // fetch initial tenant resource data
    useEffect(() => {
      if (!tenantId) {
        return;
      }
      getTenantWrappedResources({ tenantId: tenantId, awsAccountId: activeAwsAccount }).then(response => {
        const data = [];
        response.data.forEach((entry, index) => {
          const resource = entry['resourceData'];
          // fill all non-required and missing data[key] with '' so user can edit it
          for (const [key] of Object.entries(tableHeaderDisplayOptions)) {
            if (!entry[key]) {
              entry[key] = '';
            }
          }
          resource['uuid'] = entry['resourceSk'];
          resource['id'] = nanoid();
          resource['isNew'] = false;
          resource['isEdit'] = false;
          resource['isValid'] = true;
          resource['original'] = { ...resource };
          data.push(resource);
        });

        const newResource = addRowForNewResource();
        data.push(newResource);

        setTenantResourcesData(data);
      });
    }, [tenantId, activeAwsAccount]);

    return (
      <div className="resource-configuration">
        {showAwsAccountDropdown ? (
          <AwsAccountDropdown awsAccounts={awsAccounts} onChange={setActiveAwsAccount} />
        ) : null}
        <Container fluid>
          <Container fluid>
            <Row className="mt-3 mb-3">
              <Col>
                {activeAwsAccount ? (
                  <h5>
                    {resourceName} resources for account {activeAwsAccount}
                  </h5>
                ) : (
                  <h5>All {resourceName} resources</h5>
                )}
              </Col>
            </Row>
            <Row>
              <DynamicTable
                columns={tableHeaders.columns}
                initialState={tableHeaders.initialState}
                data={tenantResourcesData}
                getRowId={getRowId}
              />
              <ConfirmationDialog
                isOpen={isDeleteConfirmationDialogOpen}
                setIsOpen={setIsDeleteConfirmationDialogOpen}
                isConfirmDisabled={isDeleteConfirmationDisabled}
                text={deleteConfirmationText}
                onConfirm={deleteConfirmationCallback}
              />
              <ConfirmationDialog
                isOpen={isEditConfirmationDialogOpen}
                setIsOpen={setIsEditConfirmationDialogOpen}
                text={editConfirmationText}
                onConfirm={editConfirmationCallback}
              />
            </Row>
          </Container>
        </Container>
      </div>
    );
  };
};

export default resourceConfigurationHoc;
